import { ButtonGroup, Card, Heading, IconButton, LuIcon, toast } from "@overmap-ai/blocks"
import { type FormSubmission, OrganizationAccessLevel, type Stored, submittedAtModelSortFn } from "@overmap-ai/common"
import {
	selectCommentsOfFormSubmission,
	selectFormIdentifierValuesOfFormSubmission,
	selectFormSubmissionFieldValuesOfFormSubmission,
} from "@overmap-ai/core"
import { type UUIDFile, type UUIDPromise } from "@overmap-ai/forms"
import { FileHandler } from "@tiptap/extension-file-handler"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { type FormEventHandler, memo, useCallback, useEffect, useMemo, useState } from "react"

import { FORM_SUBMISSION_TIMELINE_ITEM_TYPES } from "../../constants"
import { FormSubmissionTimelineItemType } from "../../enums/forms"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS } from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { uuidFilesFromFiles } from "../../utils/file"
import { AttachmentButton } from "../Buttons/AttachmentButton"
import { CommentFileThumbnailList } from "../CommentFileThumbnailList/CommentFileThumbnailList"
import { FormSubmissionTimelineItemTypeMultiSelectDropdown } from "../DropdownMultiSelects/FormSubmissionTimelineItemTypeMultiSelectDropdown"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { FormSubmissionCommentTimelineItem } from "./FormSubmissionCommentTimelineItem"
import { FormSubmissionCreatedTimelineItem } from "./FormSubmissionCreatedTimelineItem"
import { FormSubmissionFieldValuesTimelineItem } from "./FormSubmissionFieldValuesTimelineItem"
import { FormSubmissionIdentifierValueTimelineItem } from "./FormSubmissionIdentifierValueTimelineItem"
import type { FormSubmissionTimelineItem, FormSubmissionTimelineSize } from "./typings"

interface FormSubmissionTimelineProps {
	formSubmission: Stored<FormSubmission>
	size: FormSubmissionTimelineSize
}

export const FormSubmissionTimeline = memo<FormSubmissionTimelineProps>((props) => {
	const { formSubmission, size } = props

	const fieldValues = useShallowEqaulSelector(selectFormSubmissionFieldValuesOfFormSubmission(formSubmission.uuid))
	const identifierValues = useShallowEqaulSelector(selectFormIdentifierValuesOfFormSubmission(formSubmission.uuid))
	const comments = useShallowEqaulSelector(selectCommentsOfFormSubmission(formSubmission.uuid))
	const [files, setFiles] = useState<UUIDFile[]>([])
	const [visibleItemTypes, setVisibleItemTypes] = useState<FormSubmissionTimelineItemType[]>(
		FORM_SUBMISSION_TIMELINE_ITEM_TYPES,
	)

	const timelineItems = useMemo(() => {
		const items: FormSubmissionTimelineItem[] = [
			{
				model: formSubmission,
				type: FormSubmissionTimelineItemType.CREATED,
			},
		]

		// NOTE: Slicing here since the first typically submitted along with the creation
		for (const fieldValue of fieldValues.toSorted(submittedAtModelSortFn).slice(1, fieldValues.length)) {
			items.push({ model: fieldValue, type: FormSubmissionTimelineItemType.FIELD_VALUES })
		}

		for (const identifierValue of identifierValues) {
			items.push({ model: identifierValue, type: FormSubmissionTimelineItemType.IDENTIFIER_VALUE })
		}

		for (const comment of comments) {
			items.push({ model: comment, type: FormSubmissionTimelineItemType.COMMENT })
		}

		return items.toSorted((a, b) => submittedAtModelSortFn(a.model, b.model)).toReversed()
	}, [comments, fieldValues, formSubmission, identifierValues])

	const filteredTimelineItems = useMemo(() => {
		const visibleItemTypesSet = new Set(visibleItemTypes)
		return timelineItems.filter(({ type }) => visibleItemTypesSet.has(type))
	}, [timelineItems, visibleItemTypes])

	const editor = useEditor({
		editorProps: {
			attributes: { class: "outline-none" },
		},
		extensions: [
			...BASE_EXTENSIONS,
			...LINK_NODES,
			...MENTION_EXTENSIONS,
			FileHandler.configure({
				onDrop: (_editor, files, _pos) => {
					setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
				},
				onPaste: (_editor, files, _htmlContent) => {
					setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
				},
			}),
			Placeholder.configure({
				placeholder: "Leave a comment",
			}),
		],
	})

	const handleAttachFiles = useCallback((files: File[]) => {
		setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
	}, [])

	const handleDeleteFile = useCallback((file: UUIDFile | UUIDPromise<UUIDFile>) => {
		setFiles((prev) => prev.filter(({ uuid }) => uuid !== file.uuid))
	}, [])

	const handleCommentSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (editor.isEmpty && files.length === 0) {
				toast.error({
					title: "Comment required",
					description: "Please add a comment before submitting.",
				})
				return
			}

			const [offlineComment, _promise] = sdk.formSubmissionComments.add({
				content: JSON.stringify(editor.getJSON()),
				form_submission: formSubmission.uuid,
				resolved: false,
			})

			if (files.length > 0) {
				void sdk.formSubmissionCommentAttachments.bulkAdd(
					files.map((file) => ({
						file,
						form_submission_comment: offlineComment.uuid,
					})),
				)
			}

			editor.commands.clearContent()
			setFiles([])
		},
		[editor, files, formSubmission.uuid],
	)

	const handleReset = useCallback(() => {
		editor.commands.clearContent()
		setFiles([])
		setVisibleItemTypes(FORM_SUBMISSION_TIMELINE_ITEM_TYPES)
	}, [editor])

	useEffect(() => {
		handleReset()
	}, [formSubmission.uuid, handleReset])

	return (
		<section className="flex w-full flex-col gap-4">
			<div className="flex items-center justify-between gap-2">
				<Heading size="sm" accentColor="base">
					Timeline
				</Heading>

				<div className="flex items-center">
					<FormSubmissionTimelineItemTypeMultiSelectDropdown
						values={visibleItemTypes}
						onValuesChange={setVisibleItemTypes}
						size="sm"
						align="end"
					>
						<IconButton variant="ghost" size={size} accentColor="base">
							<LuIcon icon={modelIconMapping.filter} />
						</IconButton>
					</FormSubmissionTimelineItemTypeMultiSelectDropdown>
				</div>
			</div>

			<div className="flex w-full flex-col gap-4">
				{filteredTimelineItems.map(({ type, model }) => {
					switch (type) {
						case FormSubmissionTimelineItemType.CREATED:
							return (
								<FormSubmissionCreatedTimelineItem
									key={model.uuid}
									formSubmission={model}
									size={size}
								/>
							)
						case FormSubmissionTimelineItemType.COMMENT:
							return (
								<FormSubmissionCommentTimelineItem
									key={model.uuid}
									formSubmissionComment={model}
									size={size}
								/>
							)
						case FormSubmissionTimelineItemType.FIELD_VALUES:
							return (
								<FormSubmissionFieldValuesTimelineItem
									key={model.uuid}
									formSubmissionFieldValues={model}
									size={size}
								/>
							)
						case FormSubmissionTimelineItemType.IDENTIFIER_VALUE:
							return (
								<FormSubmissionIdentifierValueTimelineItem
									key={model.uuid}
									formIdentifierValue={model}
									size={size}
								/>
							)
					}
				})}
			</div>

			<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
				<form className="w-full" onSubmit={handleCommentSubmit}>
					<Card className="flex h-max min-h-[72px] w-full flex-col gap-2" size="sm">
						{files.length > 0 ? (
							<CommentFileThumbnailList files={files} onDelete={handleDeleteFile} />
						) : null}

						<EditorContext value={editor}>
							<EditorContent editor={editor} className="h-max w-full grow text-sm" />
							<NewLineFloatingMenu editor={editor} />
							<TextBubbleMenu editor={editor} />
						</EditorContext>

						<ButtonGroup
							className="flex h-max w-full justify-end gap-2"
							variant="soft"
							accentColor="base"
							size={size}
						>
							<AttachmentButton
								size={size}
								icon
								variant="ghost"
								type="button"
								onAttachFiles={handleAttachFiles}
							>
								<LuIcon icon={modelIconMapping.attachment} />
							</AttachmentButton>
							<IconButton
								aria-label="submit-comment"
								type="submit"
								variant="soft"
								accentColor="base"
								size={size}
							>
								<LuIcon icon={modelIconMapping.arrowUp} />
							</IconButton>
						</ButtonGroup>
					</Card>
				</form>
			</OrganizationAccessRequired>
		</section>
	)
})
FormSubmissionTimeline.displayName = "FormSubmissionTimeline"
